import React, { useCallback, useState } from 'react';
import { ArrowRight, Loader2, Plus, Trash2 } from 'lucide-react';

import { SubstitutionExternalSkuPicker } from 'components/fbm/substitutions/SubstitutionExternalSkuPicker';
import { Button } from 'shadcn/components/ui/Button';
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from 'shadcn/components/ui/Dialog';
import { Input } from 'shadcn/components/ui/Input';
import { useCreateSubstitutionRuleMutation } from 'shared/hooks/fbm/useCreateSubstitutionRuleMutation';

let nextRowId = 0;

function createEmptySubstituteRow() {
    nextRowId += 1;
    return {
        id: `substitute-row-${nextRowId}`,
        substituteExternalSku: '',
        substituteQuantity: '',
    };
}

function resetFormState() {
    return {
        originalExternalSku: '',
        substituteRows: [createEmptySubstituteRow()],
    };
}

function isPopoverContentTarget(target) {
    return target instanceof Element && Boolean(target.closest('[data-slot="popover-content"]'));
}

function preventDialogDismissOnPopoverInteraction(event) {
    if (isPopoverContentTarget(event.target)) {
        event.preventDefault();
    }
}

/**
 * @param {Object} props
 * @param {string} props.fbmAccountName
 * @param {boolean} props.open
 * @param {(open: boolean) => void} props.onOpenChange
 * @param {(message: string) => void} [props.onError]
 * @param {(message: string) => void} [props.onSuccess]
 */
export function CreateSubstitutionRuleModal({
    fbmAccountName,
    onError = () => {},
    onOpenChange,
    onSuccess = () => {},
    open,
}) {
    const [formState, setFormState] = useState(resetFormState);
    const [validationError, setValidationError] = useState('');
    const [popoverContainer, setPopoverContainer] = useState(null);

    const { isPending, mutate: createRule } = useCreateSubstitutionRuleMutation({
        onError: (error) => {
            onError(error.message ?? 'Failed to create substitution rule');
        },
        onSuccess: () => {
            onSuccess('Substitution rule created successfully');
            setFormState(resetFormState());
            setValidationError('');
            onOpenChange(false);
        },
    });

    const handleOpenChange = useCallback(
        (nextOpen) => {
            if (!nextOpen && isPending) {
                return;
            }

            if (!nextOpen) {
                setFormState(resetFormState());
                setValidationError('');
            }
            onOpenChange(nextOpen);
        },
        [isPending, onOpenChange],
    );

    const preventDialogDismiss = useCallback(
        (event) => {
            if (isPending) {
                event.preventDefault();
                return;
            }
            preventDialogDismissOnPopoverInteraction(event);
        },
        [isPending],
    );

    const handleEscapeKeyDown = useCallback(
        (event) => {
            if (isPending) {
                event.preventDefault();
            }
        },
        [isPending],
    );

    const handleOriginalSkuChange = useCallback((value) => {
        setFormState((current) => ({
            ...current,
            originalExternalSku: value,
        }));
        setValidationError('');
    }, []);

    const handleSubstituteSkuChange = useCallback((rowId, value) => {
        setFormState((current) => ({
            ...current,
            substituteRows: current.substituteRows.map((row) =>
                row.id === rowId ? { ...row, substituteExternalSku: value } : row,
            ),
        }));
        setValidationError('');
    }, []);

    const handleSubstituteQuantityChange = useCallback((rowId, value) => {
        setFormState((current) => ({
            ...current,
            substituteRows: current.substituteRows.map((row) =>
                row.id === rowId ? { ...row, substituteQuantity: value } : row,
            ),
        }));
        setValidationError('');
    }, []);

    const handleAddSubstituteRow = useCallback(() => {
        setFormState((current) => ({
            ...current,
            substituteRows: [...current.substituteRows, createEmptySubstituteRow()],
        }));
    }, []);

    const handleRemoveSubstituteRow = useCallback((rowId) => {
        setFormState((current) => {
            if (current.substituteRows.length <= 1) {
                return current;
            }
            return {
                ...current,
                substituteRows: current.substituteRows.filter((row) => row.id !== rowId),
            };
        });
    }, []);

    const validateForm = useCallback(() => {
        const originalSku = String(formState.originalExternalSku ?? '').trim();
        if (!originalSku) {
            return 'Original external SKU is required';
        }

        const filledRows = formState.substituteRows.filter(
            (row) => String(row.substituteExternalSku ?? '').trim() || String(row.substituteQuantity ?? '').trim(),
        );

        if (filledRows.length === 0) {
            return 'At least one substitute with SKU and quantity is required';
        }

        const substitutes = [];
        const seenSubstituteSkus = new Set();

        for (const row of filledRows) {
            const substituteSku = String(row.substituteExternalSku ?? '').trim();
            const quantityRaw = String(row.substituteQuantity ?? '').trim();

            if (!substituteSku || !quantityRaw) {
                return 'Each substitute row must have both an external SKU and a quantity';
            }

            const quantity = Number(quantityRaw);
            if (!Number.isInteger(quantity) || quantity < 1) {
                return 'Substitute quantity must be a positive whole number';
            }

            if (substituteSku === originalSku) {
                return 'Original external SKU cannot also be a substitute SKU';
            }

            if (seenSubstituteSkus.has(substituteSku)) {
                return 'Duplicate substitute external SKUs are not allowed';
            }

            seenSubstituteSkus.add(substituteSku);
            substitutes.push({
                substituteExternalSku: Number(substituteSku),
                substituteQuantity: quantity,
            });
        }

        return {
            originalExternalSku: Number(originalSku),
            substitutes,
        };
    }, [formState]);

    const handleSubmit = useCallback(() => {
        const validationResult = validateForm();
        if (typeof validationResult === 'string') {
            setValidationError(validationResult);
            onError(validationResult);
            return;
        }

        setValidationError('');
        createRule({
            fbmAccountName,
            originalExternalSku: validationResult.originalExternalSku,
            substitutes: validationResult.substitutes,
        });
    }, [createRule, fbmAccountName, onError, validateForm]);

    const canRemoveRow = formState.substituteRows.length > 1;

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent
                ref={setPopoverContainer}
                className="flex max-h-[90vh] flex-col gap-0 p-0 sm:max-w-4xl data-[pending=true]:[&>button]:pointer-events-none data-[pending=true]:[&>button]:opacity-50"
                data-pending={isPending ? 'true' : 'false'}
                onEscapeKeyDown={handleEscapeKeyDown}
                onFocusOutside={preventDialogDismiss}
                onInteractOutside={preventDialogDismiss}
                onPointerDownOutside={preventDialogDismiss}
            >
                <DialogHeader className="px-6 pt-6">
                    <DialogTitle>Create substitution rule</DialogTitle>
                    <DialogDescription>
                        Select an original external SKU and one or more substitute SKUs with quantities.
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4">
                    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_2rem_minmax(0,1fr)] lg:items-stretch">
                        <section
                            className="flex h-full flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4"
                            aria-labelledby="create-rule-original-heading"
                        >
                            <h3
                                id="create-rule-original-heading"
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                            >
                                Original
                            </h3>
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                External SKU
                            </div>
                            <SubstitutionExternalSkuPicker
                                companySource={fbmAccountName}
                                enabled={open}
                                label="External SKU"
                                popoverContainer={popoverContainer}
                                showLabel={false}
                                value={formState.originalExternalSku}
                                onValueChange={handleOriginalSkuChange}
                            />
                        </section>

                        <ArrowRight
                            className="mx-auto h-5 w-5 rotate-90 self-center text-muted-foreground lg:rotate-0"
                            aria-hidden="true"
                        />

                        <section
                            className="flex h-full min-w-0 flex-col gap-4 rounded-lg border border-border bg-muted/30 p-4"
                            aria-labelledby="create-rule-substitutes-heading"
                        >
                            <h3
                                id="create-rule-substitutes-heading"
                                className="text-xs font-semibold uppercase tracking-wide text-muted-foreground"
                            >
                                Substitutes
                            </h3>
                            <div className="hidden gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:grid sm:grid-cols-[minmax(0,1fr)_5rem_2.5rem]">
                                <span>External SKU</span>
                                <span>Quantity</span>
                                <span className="sr-only">Remove</span>
                            </div>
                            <div className="flex flex-1 flex-col space-y-3">
                                {formState.substituteRows.map((row) => (
                                    <div
                                        key={row.id}
                                        className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_5rem_2.5rem] sm:items-start"
                                    >
                                        <SubstitutionExternalSkuPicker
                                            companySource={fbmAccountName}
                                            enabled={open}
                                            label="External SKU"
                                            popoverContainer={popoverContainer}
                                            showLabel={false}
                                            value={row.substituteExternalSku}
                                            onValueChange={(value) => handleSubstituteSkuChange(row.id, value)}
                                        />
                                        <Input
                                            type="number"
                                            min={1}
                                            step={1}
                                            inputMode="numeric"
                                            aria-label="Substitute quantity"
                                            placeholder="Qty"
                                            className="w-full"
                                            value={row.substituteQuantity}
                                            onChange={(event) =>
                                                handleSubstituteQuantityChange(row.id, event.target.value)
                                            }
                                        />
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="icon"
                                            className="justify-self-end sm:justify-self-auto"
                                            aria-label="Remove substitute row"
                                            disabled={!canRemoveRow || isPending}
                                            onClick={() => handleRemoveSubstituteRow(row.id)}
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ))}

                                <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="mt-auto w-fit"
                                    disabled={isPending}
                                    onClick={handleAddSubstituteRow}
                                >
                                    <Plus className="mr-1 h-4 w-4" />
                                    Add
                                </Button>
                            </div>
                        </section>
                    </div>

                    {validationError ? <p className="mt-4 text-sm text-destructive">{validationError}</p> : null}
                </div>

                <DialogFooter className="border-t px-6 py-4">
                    <DialogClose asChild>
                        <Button type="button" variant="outline" disabled={isPending}>
                            Cancel
                        </Button>
                    </DialogClose>
                    <Button type="button" disabled={isPending} onClick={handleSubmit}>
                        {isPending ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                                Creating…
                            </>
                        ) : (
                            'Create Rule'
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

CreateSubstitutionRuleModal.displayName = 'CreateSubstitutionRuleModal';
